const API_URL = process.env.REACT_APP_API_URL || '';

export interface Memory {
  id: number;
  user_id: string;
  type: 'message' | 'photo' | 'music' | 'love';
  title: string;
  content: string;
  image_url?: string;
  music_title?: string;
  music_artist?: string;
  music_url?: string;
  location_lat?: number;
  location_lng?: number;
  location_address?: string;
  created_at: string;
  updated_at: string;
  userName: string;
  userAvatar: string;
}

export interface NewMemoryData {
  type: 'message' | 'photo' | 'music' | 'love';
  title: string;
  content: string;
  imageUrl?: string;
  musicTitle?: string;
  musicArtist?: string;
  musicUrl?: string;
  locationLat?: number;
  locationLng?: number;
  locationAddress?: string;
}

class MemoryService {
  private getAuthHeaders() {
    // Directly access the token from localStorage to ensure it's always the latest.
    const savedAuth = localStorage.getItem('loveMazeAuth');
    if (!savedAuth) {
      throw new Error('User is not authenticated. Cannot make API calls.');
    }
    
    const { token } = JSON.parse(savedAuth);
    if (!token) {
      // This case should be handled by the UI logic (e.g., redirect to login)
      // Throwing an error here is a safeguard.
      throw new Error('User is not authenticated. Cannot make API calls.');
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  async getMemories(): Promise<Memory[]> {
    const response = await fetch(`${API_URL}/api/memories`, {
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      // Handle specific HTTP errors if needed
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch memories' }));
      throw new Error(errorData.message || 'Failed to fetch memories');
    }

    const data = await response.json();
    return data.data || [];
  }

  async createMemory(memoryData: NewMemoryData): Promise<{ success: boolean }> {
    const response = await fetch(`${API_URL}/api/memories`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(memoryData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to create memory' }));
      throw new Error(errorData.message || 'Failed to create memory');
    }

    return response.json();
  }

  async uploadImage(imageFile: File): Promise<{ success: boolean; url: string }> {
    const formData = new FormData();
    formData.append('image', imageFile);

    const authHeaders = this.getAuthHeaders();
    // When using FormData with fetch, the browser automatically sets the Content-Type with the correct boundary.
    // Manually setting 'Content-Type': 'application/json' will cause the request to fail.
    // We create a new headers object without the 'Content-Type' to avoid this issue.
    const { 'Content-Type': _, ...uploadHeaders } = authHeaders;


    const response = await fetch(`${API_URL}/api/upload-image`, {
      method: 'POST',
      headers: uploadHeaders,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to upload image' }));
      throw new Error(errorData.message || 'Failed to upload image');
    }

    return response.json();
  }

  async registerFCMToken(fcmToken: string): Promise<{ success: boolean }> {
    const response = await fetch(`${API_URL}/api/notifications/register`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ fcmToken })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to register FCM token' }));
      throw new Error(errorData.message || 'Failed to register FCM token');
    }

    return response.json();
  }
}

export const memoryService = new MemoryService();
